import React, { useState, useEffect, useMemo, useCallback } from "react";
import Swal from "sweetalert2";
import api from "../../../../../utils/axios";
import LocationModal from "../../LocationModal";
import { getData } from "../../../../../utils/SamuiUtils";

export default function ServiceLocationModal({ isOpen, onClose, arId, onSelect, locations: externalLocations, purpose = "2" }) {
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [locationModalKey, setLocationModalKey] = useState(0); // ใช้สำหรับ force re-render LocationModal
  const [autoOpenPurpose, setAutoOpenPurpose] = useState(null); // เก็บ purpose เมื่อเปิด modal โดยอัตโนมัติ
  const [autoOpenDisabled, setAutoOpenDisabled] = useState(false);
  const [autoOpenedOnce, setAutoOpenedOnce] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([]);

  // สร้าง location object ใหม่ทุกครั้งที่ purpose หรือ isCreatingNew เปลี่ยน (อยู่นอก conditional rendering)
  const locationForModal = useMemo(() => {
    if (isCreatingNew) {
      return { ServicePurpose: String(purpose) };
    }
    return editLocation;
  }, [isCreatingNew, purpose, editLocation]);

  // State สำหรับเก็บ location ที่จะส่งไปยัง LocationModal (ใช้เมื่อกดปุ่มสร้างใหม่)
  const [newLocationForModal, setNewLocationForModal] = useState(null);

  // ฟังก์ชันสำหรับ fetch และ refresh locations ตาม purpose
  const refreshLocations = useCallback(async () => {
    if (!arId) return;
    try {
      const refreshed = await api.get(`/get-tb-set-service-addr/${arId}`);
      if (refreshed.data.status === "OK") {
        const allLocations = refreshed.data.result?.recordset || [];
        // Filter locations ตาม purpose
        const filteredLocations = allLocations.filter((loc) =>
          String(loc.ServicePurpose) === String(purpose)
        );
        setLocations(filteredLocations);
      }
    } catch (err) {
      console.error("Refresh locations error:", err);
    }
  }, [arId, purpose]);

  // เมื่อเปิด modal ให้ refresh locations เพื่อให้ได้ข้อมูลล่าสุด
  useEffect(() => {
    if (isOpen && arId) {
      refreshLocations();
    } else if (!isOpen) {
      // Reset locations เมื่อปิด modal
      setLocations([]);
      setAutoOpenDisabled(false);
      setAutoOpenedOnce(false);
    }
  }, [isOpen, arId, refreshLocations]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchServiceTypes = async () => {
      try {
        const res = await getData(
          "Tb_Set_ServiceAddrType",
          "ServiceAddr_Type_Status = 'Y'"
        );
        const rows = Array.isArray(res) ? res : res?.data || [];
        const mapped = rows
          .map((item) => ({
            id: item?.ServiceAddr_Type_Id ?? item?.id ?? item?.Id,
            name:
              item?.ServiceAddr_Type_Name ??
              item?.name ??
              item?.ServiceAddr_Type,
          }))
          .filter((item) => item.id && item.name);
        setServiceTypes(mapped);
      } catch (err) {
        console.error("โหลดประเภทสถานที่บริการไม่สำเร็จ:", err);
        setServiceTypes([]);
      }
    };
    fetchServiceTypes();
  }, [isOpen]);

  // เมื่อเปิด modal และไม่มีข้อมูล ให้เปิดฟอร์มสร้างใหม่โดยอัตโนมัติ
  useEffect(() => {
    if (!isOpen || !arId || locationModalOpen || autoOpenDisabled || autoOpenedOnce) {
      setIsCreatingNew(false);
      setAutoOpenPurpose(null);
      setNewLocationForModal(null);
      return;
    }

    // รอให้ refreshLocations ทำงานก่อน (ใช้ setTimeout เพื่อให้ API call เสร็จ)
    const timer = setTimeout(() => {
      if (locations.length === 0) {
        // เก็บ purpose เมื่อเปิด modal โดยอัตโนมัติ
        setAutoOpenedOnce(true);
        setAutoOpenPurpose(purpose);
        // สร้าง location object ใหม่ด้วย purpose ปัจจุบัน (ต้องเป็น string)
        const newLocation = { ServicePurpose: String(purpose) };
        setNewLocationForModal(newLocation);
        setIsCreatingNew(true);
        setLocationModalKey(prev => prev + 1); // เพิ่ม key เพื่อ force re-render
        // ใช้ setTimeout เพื่อให้แน่ใจว่า state update แล้วก่อนเปิด modal
        setTimeout(() => {
          setLocationModalOpen(true);
        }, 50);
      }
    }, 500); // รอให้ refreshLocations ทำงานก่อน (API call อาจใช้เวลา)

    return () => clearTimeout(timer);
  }, [isOpen, locations.length, arId, purpose, locationModalOpen, autoOpenDisabled, autoOpenedOnce]);


  if (!isOpen) return null;

  const formatAddress = (loc) => {
    return [
      loc.AddrNo ? `เลขที่ ${loc.AddrNo}` : "",
      loc.Building ? `อาคาร ${loc.Building}` : "",
      loc.Village ? `หมู่บ้าน ${loc.Village}` : "",
      loc.Moo ? `ม.${loc.Moo}` : "",
      loc.Soi ? `ซ.${loc.Soi}` : "",
      loc.Road ? `ถ.${loc.Road}` : "",
      loc.SubDistrictName ? `ต.${loc.SubDistrictName}` : "",
      loc.DistrictName ? `อ.${loc.DistrictName}` : "",
      loc.ProvinceName ? `จ.${loc.ProvinceName}` : "",
      loc.Zipcode || "",
    ]
      .filter(Boolean)
      .join(" ");
  };

  const filtered = locations.filter((loc) =>
    formatAddress(loc).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <div className={`modal fade show`} style={{ display: "block" }} tabIndex="-1">
        <div
          className="modal-dialog modal-dialog-centered"
          style={{
            maxWidth: "1100px",
            width: "95%",
          }}
        >
          <div className="modal-content border-0 shadow-lg">

            {/*  CSS override */}
            <style>
              {`
                .service-location-table {
                  width: 100% !important;
                  min-width: unset !important;
                  table-layout: auto !important;
                }
                .service-location-table th,
                .service-location-table td {
                  white-space: normal !important;
                  word-break: break-word !important;
                  text-align: left !important;
                  font-size: 14px !important;
                  padding: 6px 8px !important;
                }
                .service-location-table thead th {
                  background-color: #E13F57 !important;
                  color: white !important;
                  font-weight: 600 !important;
                  position: sticky;
                  top: 0;
                  z-index: 5;
                }
              `}
            </style>

            <div className="modal-header" style={{ backgroundColor: "#E13F57", color: "white" }}>
              <h5 className="modal-title" style={{ fontWeight: "bold" }}>เลือกสถานที่บริการ</h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body">

              <input
                type="text"
                className="form-control mb-3"
                placeholder="ค้นหาสถานที่บริการ..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ height: "36px", fontSize: "14px" }}
              />

              {/*  ไม่มี scroll ซ้าย-ขวา */}
              <div
                className="table-responsive"
                style={{ maxHeight: "350px", overflowY: "auto", overflowX: "hidden" }}
              >
                <table className="table table-striped table-hover mb-0 service-location-table">

                  <thead>
                    <tr>
                      <th style={{ width: "22%" }}>ชื่อสถานที่บริการ</th>
                      <th style={{ width: "14%", textAlign: "center" }}>จังหวัด</th>
                      <th style={{ width: "14%", textAlign: "center" }}>เบอร์โทร</th>
                      <th style={{ width: "36%" }}>ที่อยู่</th>
                      <th style={{ width: "14%", textAlign: "center" }}>การจัดการ</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filtered.length > 0 ? (
                      filtered.map((loc) => (
                        <tr key={loc.ServiceAddr_Id}>
                          <td>{loc.ServiceAddr_Name || "-"}</td>
                          <td style={{ textAlign: "center" }}>{loc.ProvinceName || "-"}</td>
                          <td style={{ textAlign: "center" }}>{loc.AR_Tel || "-"}</td>
                          <td>{formatAddress(loc)}</td>
                          <td style={{ textAlign: "center" }}>
                            <div className="d-flex justify-content-center gap-2">
                              <button
                                className="btn btn-sm"
                                style={{ backgroundColor: "#F17F25", color: "white", width: "70px", fontSize: "14px" }}
                                onClick={() => {
                                  onSelect(
                                    {
                                      ServiceAddr_Id: loc.ServiceAddr_Id,
                                      ServiceAddr_Name: loc.ServiceAddr_Name,
                                      ServiceAddr_FullName: formatAddress(loc),
                                      AR_Id: arId,
                                      AR_ContactName: loc.AR_ContactName || "",
                                      AR_Tel: loc.AR_Tel || "",
                                    },
                                    true
                                  );

                                  onClose();
                                }}
                              >
                                เลือก
                              </button>

                              <button
                                className="btn btn-sm btn-warning"
                                style={{ width: "70px", color: "white", fontSize: "14px" }}
                                onClick={() => {
                                  setEditLocation(loc);
                                  setLocationModalOpen(true);
                                }}
                              >
                                แก้ไข
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-3 text-muted">
                          ไม่พบข้อมูล
                        </td>
                      </tr>
                    )}
                  </tbody>

                </table>
              </div>

            </div>

            <div className="modal-footer d-flex justify-content-between">
              <button
                className="btn"
                style={{ backgroundColor: "#F17F25", color: "white", borderColor: "#F17F25" }}
                onClick={() => {
                  setIsCreatingNew(true);
                  setEditLocation(null);
                  setNewLocationForModal({ ServicePurpose: String(purpose) });
                  setLocationModalKey(prev => prev + 1);
                  setLocationModalOpen(true);
                }}
              >
                + สร้างใหม่
              </button>
              <button
                className="btn"
                style={{ backgroundColor: "#E13F57", color: "white", borderColor: "#E13F57" }}
                onClick={onClose}
              >
                ปิด
              </button>
            </div>

          </div>
        </div>
      </div>

      {/*  Backdrop */}
      {isOpen && (
        <div className="modal-backdrop fade show"></div>
      )}


      {/*  Modal แก้ไข/สร้างสถานที่ */}
      {locationModalOpen && (
        <LocationModal
          key={`location-modal-${purpose}-${isCreatingNew ? 'new' : editLocation?.ServiceAddr_Id || 'none'}-${locationModalKey}-${newLocationForModal?.ServicePurpose || 'none'}`} // Force re-render เมื่อ purpose, mode, key หรือ newLocationForModal เปลี่ยน
          isOpen={locationModalOpen}
          onClose={() => {
            setLocationModalOpen(false);
            setEditLocation(null);
            setIsCreatingNew(false);
            setNewLocationForModal(null); // Reset newLocationForModal เมื่อปิด modal
            setAutoOpenPurpose(null); // Reset autoOpenPurpose เมื่อปิด modal
            if (autoOpenedOnce && locations.length === 0) {
              setAutoOpenDisabled(true);
              setAutoOpenedOnce(false);
            }
            // ถ้าเป็นการสร้างใหม่และไม่ได้ถูก auto-open ให้ปิด modal หลักด้วย
            if (isCreatingNew && !autoOpenedOnce) {
              onClose();
            }
          }}
          ServiceAddr_Id={editLocation?.ServiceAddr_Id || null}
          AR_Id={arId}
          defaultServicePurpose={String(purpose)} // ส่ง purpose ไปเสมอเพื่อให้ LocationModal ใช้ purpose ที่ถูกต้อง
          location={
            isCreatingNew 
              ? null // ไม่ส่ง location เมื่อ isCreatingNew เพื่อให้ defaultServicePurpose ถูกใช้
              : (editLocation || null)
          } // ใช้ editLocation เมื่อแก้ไข, ไม่ส่ง location เมื่อสร้างใหม่
          serviceTypes={serviceTypes}
          onSave={async (formData) => {
            try {
              // ตั้งค่า ServicePurpose ตาม purpose ที่เลือก (2 = สถานที่บริการ, 3 = ที่อยู่ตามสัญญา)
              const dataToSave = {
                ...formData,
                AR_Id: arId,
                ServicePurpose: purpose, // ใช้ purpose ที่ส่งมา (2 หรือ 3)
                Comp_Id: formData.Comp_Id || 1,
              };

              let res;
              if (formData.ServiceAddr_Id) {
                res = await api.put(`/update-tb-set-service-addr/${formData.ServiceAddr_Id}`, dataToSave);
              } else {
                res = await api.post(`/create-tb-set-service-addr`, dataToSave);
              }

              if (res.data.status === "OK") {
                // หาสถานที่ที่เพิ่งสร้าง/แก้ไข
                const newServiceAddrId = formData.ServiceAddr_Id || res.data.ServiceAddr_Id;
                
                // รีเฟรชรายการสถานที่ใน Service2ndModal
                await refreshLocations();
                
                // รีเฟรชรายการสถานที่เพื่อให้ได้ข้อมูลล่าสุด (รวม contact info) สำหรับ onSelect
                const refreshed = await api.get(`/get-tb-set-service-addr/${arId}`);
                if (refreshed.data.status === "OK") {
                  const allLocations = refreshed.data.result?.recordset || [];
                  
                  // หาสถานที่ที่เพิ่งสร้าง/แก้ไข
                  const newLocation = allLocations.find(
                    (loc) => loc.ServiceAddr_Id === newServiceAddrId
                  );

                  if (newLocation) {
                    // Auto-select สถานที่ที่เพิ่งสร้าง/แก้ไข และบอก parent ให้ refresh
                    await onSelect(
                      {
                        ServiceAddr_Id: newLocation.ServiceAddr_Id,
                        ServiceAddr_Name: newLocation.ServiceAddr_Name,
                        ServiceAddr_FullName: formatAddress(newLocation),
                        AR_Id: arId,
                        AR_ContactName: newLocation.AR_ContactName || "",
                        AR_Tel: newLocation.AR_Tel || "",
                      },
                      true // needsReload = true เพื่อให้ parent refresh
                    );
                  } else {
                    // ถ้าไม่พบ location ให้ refresh parent ไว้ก่อน
                    await onSelect(
                      {
                        ServiceAddr_Id: newServiceAddrId,
                        ServiceAddr_Name: formData.ServiceAddr_Name,
                        ServiceAddr_FullName: formatAddress(formData),
                        AR_Id: arId,
                        AR_ContactName: "",
                        AR_Tel: "",
                      },
                      true
                    );
                  }
                }

                setLocationModalOpen(false);
                setEditLocation(null);
                setIsCreatingNew(false);

                // ถ้าเป็นการสร้างใหม่ ให้ปิด modal หลักด้วย
                if (isCreatingNew) {
                  onClose();
                }

                return { status: "OK", ServiceAddr_Id: formData.ServiceAddr_Id || res.data.ServiceAddr_Id };
              }

              return { status: "FAILED" };

            } catch (err) {
              console.error("Save error:", err);
              Swal.fire("ผิดพลาด", "ไม่สามารถบันทึกข้อมูลได้", "error");
              return { status: "FAILED" };
            }
          }}
          onReload={async () => {
            // รีเฟรชรายการเมื่อมีการเปลี่ยนแปลง
            await refreshLocations();
          }}
        />
      )}
    </>
  );
}
